// Entry de PRERENDER (SSG) para vite-prerender-plugin. Se ejecuta en Node
// durante el build; NO importa main.tsx (que hace trabajo browser-only: SW,
// Sentry, listeners PWA). Monta el árbol interno (AppInner) bajo StaticRouter
// para la URL pedida y serializa los meta de react-helmet al <head>.
//
// Importante: usamos renderToPipeableStream + onAllReady para ESPERAR a los
// chunks lazy de las spokes (renderToString no espera Suspense → saldrían
// vacías). El idioma lo fija <LangLayout> a partir de la URL (es/ca/en).
import { renderToReadableStream } from "react-dom/server";
import { StaticRouter } from "react-router-dom/server";
import { HelmetProvider, type HelmetServerState } from "react-helmet-async";
import { AppInner } from "./App";
import { ORGANIZATION_JSON_LD } from "@/components/landing/SEO";
import { localeFromPath } from "@/lib/locale";
import landingI18n, { loadLandingLanguage } from "@/lib/i18n-landing";
import type { PrerenderArguments, PrerenderResult } from "vite-prerender-plugin";

async function renderToHtml(node: React.ReactElement): Promise<string> {
  // renderToReadableStream usa web streams (sin node:stream → compatible con el
  // entorno browser-like del plugin) y `allReady` espera a TODO el Suspense/lazy
  // (las spokes son lazy; con renderToString saldrían vacías).
  let caught: unknown = null;
  const stream = await renderToReadableStream(node, {
    onError(err) {
      caught = err;
    },
  });
  await stream.allReady;
  if (caught) throw caught;
  return await new Response(stream).text();
}

// Convierte un HelmetDatum (meta/link/script) a los HeadElement del plugin.
type HeadEl = { type: string; props: Record<string, string>; children?: string };
// React (toComponent) usa nombres de prop camelCase; el atributo HTML real es
// en minúsculas. Normalizamos los que aparecen en nuestros <meta>/<link>.
const ATTR_MAP: Record<string, string> = {
  hrefLang: "hreflang",
  charSet: "charset",
  httpEquiv: "http-equiv",
  className: "class",
};
function toHeadElements(datum: { toComponent: () => unknown }): HeadEl[] {
  const els = (datum.toComponent() as React.ReactElement[]) ?? [];
  return els.map((el) => {
    const { children, key, ...rest } = (el.props ?? {}) as Record<string, unknown>;
    const props: Record<string, string> = {};
    for (const [k, v] of Object.entries(rest)) {
      if (v == null || v === false) continue;
      props[ATTR_MAP[k] ?? k] = v === true ? "" : String(v);
    }
    const out: HeadEl = { type: el.type as string, props };
    if (children != null) {
      out.children = Array.isArray(children) ? children.join("") : String(children);
    }
    return out;
  });
}

export async function prerender(data: PrerenderArguments): Promise<PublicPrerenderResult> {
  const url = data?.url || "/";
  const helmetContext: { helmet?: HelmetServerState } = {};

  // LDT-718 · El idioma se fija desde la URL ANTES de pintar, como hace
  // main.tsx en el navegador. Antes lo cambiaba <LangLayout> a mitad del
  // render, cuando el banner de cookies (que va delante) ya se había pintado
  // en el idioma de la página anterior del bucle: /ca salía con el banner en
  // castellano y /en en catalán. Y al hidratar, eso descuadraría.
  const lang = localeFromPath(url);
  await loadLandingLanguage(lang);
  await landingI18n.changeLanguage(lang);

  const html = await renderToHtml(
    <HelmetProvider context={helmetContext}>
      {/* LDT-718 · Los mismos providers que App.tsx pone alrededor de la
          landing: React Query y el TooltipProvider global ya solo los usa la
          app (Plataforma.tsx). */}
      <StaticRouter location={url}>
        <AppInner />
      </StaticRouter>
    </HelmetProvider>,
  );

  const helmet = helmetContext.helmet!;
  const titleChildren = (helmet.title.toComponent() as unknown as React.ReactElement[])?.[0]
    ?.props?.children as unknown;
  const title = Array.isArray(titleChildren)
    ? titleChildren.join("")
    : String(titleChildren ?? "");

  const elements = new Set<HeadEl>([
    ...toHeadElements(helmet.meta),
    ...toHeadElements(helmet.link),
    ...toHeadElements(helmet.script),
    // Organization JSON-LD sitewide (vive en App, fuera de AppInner) → lo
    // añadimos a cada página estática.
    {
      type: "script",
      props: { type: "application/ld+json" },
      children: JSON.stringify(ORGANIZATION_JSON_LD),
    },
  ]);

  return {
    html,
    head: { lang: localeFromPath(url), title, elements },
  };
}

// El tipo PrerenderResult del plugin usa Set<HeadElement>; reutilizamos el
// alias local para evitar fricción de tipos entre versiones.
type PublicPrerenderResult = Extract<PrerenderResult, { html: string }>;
